/**
 * PendingListPage — S4 / FR-4 (proposal §3.10).
 *
 * Reached in ONE click from the portfolio's Pending `.count-link` chip
 * (same affordance as the Failed drill, J3 rule). Mirrors the failed
 * list's page frame (DeploymentDetail) exactly — the visual identity
 * reinforces that this is the same kind of "list of invoices at one
 * deployment, scoped to one period".
 *
 * Differences from the failed list:
 * - 5 columns, not 6 (no error code / error message; the source holds
 *   none for pending invoices — design README S4).
 * - The Pending chip links here, not to /deployment/{dbName} (the
 *   failed-list root). Routing grammar is additive — see route.ts.
 *
 * The aging signal lives in the SUBMITTED cell (formatTimestamp +
 * muted relative age); no error columns means no unmapped-code
 * handling on this view.
 */

import { useEffect, useMemo } from 'react'
import { routePeriodUsable, useRoute } from '@components/route'
import { AppHeader } from '@components/AppHeader'
import { Breadcrumbs, listCrumbs } from '@components/Breadcrumbs'
import { BranchRefresh } from '@components/BranchRefresh'
import { PendingInvoiceList } from '@components/InvoiceList'
import { Pagination } from '@components/Pagination'
import { branchFreshness } from '@domain/tpinBranch'
import { EmptyState, ErrorState, LoadingState, RefreshingFrame } from '@components/RealStates'
import { useBranchContext } from '@components/useBranchContext'
import { tinOfBranch } from '@domain/tpinPaths'
import { useInvoiceListStore } from '@stores/invoiceListStore'

export function PendingListPage({ dbName }: { dbName: string }) {
  const route = useRoute()
  const period = route.period

  const invoiceListView = useInvoiceListStore((s) => s.page)
  const loadInvoices = useInvoiceListStore((s) => s.load)

  const rangeVerdict = routePeriodUsable(period)
  // Memoize on period primitives — a stable reference keeps the load
  // effect from re-running on every render (routePeriodUsable returns
  // a fresh object; only the underlying preset/from/to identity
  // matters for the query key).
  const fromTo = useMemo(
    () => (rangeVerdict.usable ? rangeVerdict.resolved : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [period.preset, period.from, period.to],
  )

  // An unusable range is the empty string: no real date is empty, so no branch read is made for it.
  const from = fromTo === null ? '' : fromTo.from
  const to = fromTo === null ? from : fromTo.to
  const branch = useBranchContext(tinOfBranch(dbName), dbName, from, to)

  useEffect(() => {
    if (fromTo === null) return
    void loadInvoices(dbName, fromTo, { status: 'PENDING' })
  }, [dbName, loadInvoices, fromTo])

  const page =
    invoiceListView.kind === 'ready'
      ? invoiceListView.data
      : invoiceListView.kind === 'refreshing'
        ? invoiceListView.previous
        : null

  const scopeText = `Deployment ${dbName}, invoices created ${
    fromTo !== null && fromTo.from === fromTo.to ? `on ${fromTo.from}` : `between ${fromTo?.from} and ${fromTo?.to}`
  }`

  const handleRefresh = () => {
    if (fromTo === null) return
    branch.refresh()
    void loadInvoices(dbName, fromTo, { status: 'PENDING' })
  }

  // The reference clock for the table's relative-age formatter — use
  // now() at render time so the relative labels update on next mount
  // (acceptable for the per-page experience; not for live ticking).
  const now = useMemo(() => new Date(), [])

  return (
    <div className="page page-pending">
      <AppHeader period={period} />
      <main className="page-main">
        <BranchRefresh
          freshness={branchFreshness(branch.lookup)}
          refreshing={invoiceListView.kind === 'refreshing' || branch.refreshing}
          onRefresh={handleRefresh}
        />

        {/* Breadcrumb, then the title: no period selector here - the period is set on the branch view (PD-36 N1). */}
        <Breadcrumbs
          // TPIN > Branch > list: the worklist's parent is its S-BRANCH view (TL ruling A2).
          crumbs={listCrumbs(
            dbName,
            'Pending invoices',
          )}
          period={period}
        />

        <div className="page-title-row">
          <h1 className="page-title" data-testid="pending-title">{`Pending invoices — ${dbName}`}</h1>
          {fromTo !== null && (
            <p className="page-scope" data-testid="pending-scope">
              Invoices created {fromTo.from === fromTo.to ? `on ${fromTo.from}` : `between ${fromTo.from} and ${fromTo.to}`} — the created date owns the period.
            </p>
          )}
        </div>

        {/* The period travels in the URL from the branch view; this list does not change it. */}

        {!rangeVerdict.usable ? (
          <ErrorState message={rangeVerdict.reason} />
        ) : (
          <RefreshingFrame refreshing={invoiceListView.kind === 'refreshing'}>
            {invoiceListView.kind === 'loading' && (
              <LoadingState label={`Loading pending invoices for ${dbName}…`} />
            )}
            {invoiceListView.kind === 'error' && (
              <ErrorState
                message={invoiceListView.message}
                onRetry={() => fromTo !== null && void loadInvoices(dbName, fromTo, { status: 'PENDING' })}
              />
            )}
            {(invoiceListView.kind === 'ready' || invoiceListView.kind === 'refreshing') && page !== null && (
              <>
                {page.invoices.length === 0 ? (
                  <EmptyState scope={scopeText} />
                ) : (
                  <>
                    <PendingInvoiceList rows={page.invoices} dbName={dbName} now={now} />
                    <Pagination
                      page={page}
                      onCursor={(cursor) =>
                        fromTo !== null && void loadInvoices(dbName, fromTo, { cursor, status: 'PENDING' })
                      }
                    />
                  </>
                )}
              </>
            )}
          </RefreshingFrame>
        )}
      </main>
    </div>
  )
}

export default PendingListPage
